/**
 * Variations panel (DNKADM-0055 U3) — lists a service's `v2_service_variation` rows and
 * hosts the add/edit dialog, the delete affordance, and (per variation) the per-staff
 * price-exception editor (handoff §5.5).
 *
 * **Exactly-one-default invariant.** The panel guarantees a service keeps exactly one
 * default. When a saved variation becomes the default, any OTHER previous default is
 * cleared (a `PUT` that flips its `isDefault` to false). The {@link VariationDialog}
 * blocks clearing the only default. The first variation a service gets is forced to be
 * the default.
 *
 * Money/credits render via the 0049 helpers (`formatMoney` with the tenant currency
 * from `useTenantCurrency`, `formatCredits`).
 *
 * **The Lengths and prices card (DNKADM-0493).** On the service page a variation is a
 * "length". This panel renders as that read-only card: a table (length, price, credits,
 * no-show fee), an Edit per row that opens the length drawer ({@link VariationDialog}), and
 * Add length. Duplicate and Remove stay as row actions. The per-coach, weekday and date
 * price editors moved to the Special prices card (`SpecialPricesCard`), so this card no
 * longer expands. `onChanged` fires after every change so that card re-reads the lengths.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { formatCredits, formatMoney } from '@/utils/v2Format';
import {
  copyVariation,
  deleteVariation,
  listVariations,
  updateVariation,
  type ServiceVariation,
  type VariationDisplay,
} from '@/api/services';
import { VariationDialog } from './VariationDialog';
import { SummaryCard } from './SummaryCard';
import { ScreenError } from '@/components/ScreenError';

export interface VariationsPanelProps {
  serviceId: string;
  /** Fired after any length change (add, edit, duplicate, remove) — DNKADM-0493. */
  onChanged?: () => void;
  /**
   * The service's `variationDisplay` (DNKADM-0339 U2). A `duration` ladder (the default when the
   * wire omits it) renders ascending by `durationMinutes`; a `type` set (Club Fitting: Component /
   * Full Bag) keeps the API's catalog order, which may be intentional.
   */
  variationDisplay?: VariationDisplay | undefined;
}

/**
 * The rows in render order (DNKADM-0339 U1): a fixed, sensible default — not a preference. The API
 * returns effectively name order, which buries "30 Minute" between "3.5 Hour" and "4 Hour" on a
 * duration ladder. A stable sort on a COPY, so ties keep their relative order and the fetched
 * collection is never mutated. Row identity/actions are keyed by `v.id`, never by index.
 */
function orderForDisplay(rows: ServiceVariation[], display: VariationDisplay): ServiceVariation[] {
  if (display === 'type') return rows;
  return [...rows].sort((a, b) => a.durationMinutes - b.durationMinutes);
}

/** Build the update body from an existing variation, overriding `isDefault`. */
function withDefault(v: ServiceVariation, isDefault: boolean) {
  return {
    name: v.name,
    isDefault,
    durationMinutes: v.durationMinutes,
    preBufferMinutes: v.preBufferMinutes,
    postBufferMinutes: v.postBufferMinutes,
    price: v.price,
    noShowFee: v.noShowFee,
    creditCost: v.creditCost,
  };
}

function noShowFeeLabel(fee: number | null, currency: string | null): string {
  if (fee === null) return 'Booked price';
  if (fee === 0) return 'No fee';
  return currency ? formatMoney(fee, currency) : fee.toFixed(2);
}

export function VariationsPanel({ serviceId, onChanged, variationDisplay = 'duration' }: VariationsPanelProps) {
  const showToast = useToast();
  const { currency } = useTenantCurrency();

  const variations = useResource(
    useCallback(() => listVariations(serviceId), [serviceId]),
    [serviceId],
  );

  const [dialog, setDialog] = useState<{ variation: ServiceVariation | null } | null>(null);

  const data = useMemo(
    () => orderForDisplay(variations.data ?? [], variationDisplay),
    [variations.data, variationDisplay],
  );
  const defaults = data.filter((v) => v.isDefault);
  const refetchList = variations.refetch;
  const refetch = useCallback(() => {
    refetchList();
    onChanged?.();
  }, [refetchList, onChanged]);
  // A brand-new service's first variation must be the default.
  const isFirstVariation = data.length === 0;

  /**
   * After a variation save, repair the single-default invariant. The first variation a
   * service gets is forced to be the default. When the just-saved variation became the
   * default, any OTHER variation still marked default is cleared (the just-saved one
   * wins). Then refetch.
   */
  const onVariationSaved = useCallback(
    async (saved: ServiceVariation | null, wasDefault: boolean) => {
      const savedId = saved?.id ?? null;
      try {
        if (isFirstVariation && savedId !== null && !wasDefault) {
          // Force the first variation to be the default.
          await updateVariation(serviceId, savedId, withDefault({ ...saved! }, true));
        } else if (wasDefault && savedId !== null) {
          const stale = data.filter((v) => v.isDefault && v.id !== savedId);
          await Promise.all(
            stale.map((v) => updateVariation(serviceId, v.id, withDefault(v, false))),
          );
        }
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t update the default length.', 'error');
      } finally {
        refetch();
      }
    },
    [data, serviceId, refetch, showToast, isFirstVariation],
  );

  async function handleDelete(variation: ServiceVariation) {
    try {
      await deleteVariation(serviceId, variation.id);
      // If we removed the default, promote the first remaining variation.
      if (variation.isDefault) {
        const remaining = data.filter((v) => v.id !== variation.id);
        const next = remaining[0];
        if (next) await updateVariation(serviceId, next.id, withDefault(next, true));
      }
      refetch();
      showToast('Length removed', 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t remove the length.', 'error');
    }
  }

  /**
   * Duplicate a variation in ONE click. The backend deep-clones the base fields + all
   * price exceptions (per-staff/day/date) in the single copy POST — the FE never reads
   * or re-PUTs the source's exceptions. The copy is `isDefault:false` by contract, so
   * the single-default invariant is unaffected (no default repair). Refetch surfaces the
   * new "… (Copy)" row.
   */
  async function handleDuplicate(variation: ServiceVariation) {
    try {
      await copyVariation(serviceId, variation.id);
      refetch();
      showToast('Length duplicated', 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t duplicate the length.', 'error');
    }
  }

  const addLength = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<Icon name="add" />}
      onClick={() => setDialog({ variation: null })}
    >
      Add length
    </Button>
  );

  return (
    <>
      <SummaryCard
        title="Lengths and prices"
        noEdit
        onEdit={() => {}}
        action={addLength}
        // A service with no lengths yet collapses to one line, with Add length beside it.
        empty={
          variations.status === 'ready' && data.length === 0
            ? 'No lengths yet. Add at least one; the first one is the default.'
            : undefined
        }
      >
        {variations.status === 'loading' && (
          <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
        )}

        {variations.status === 'error' && (
          <ScreenError
            error={variations.error}
            what="the lengths"
            onRetry={variations.refetch}
            sx={{ alignItems: 'center' }}
          />
        )}

        {variations.status === 'ready' && data.length > 0 && (
          <>
            {defaults.length !== 1 && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {defaults.length === 0
                  ? 'No default length. Mark one length as the default.'
                  : 'More than one default length. Keep exactly one as the default.'}
              </Alert>
            )}
            <Table size="small" aria-label="Lengths and prices">
              <TableHead>
                <TableRow>
                  <TableCell>Length</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell align="right">No-show fee</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((v) => {
                  const label = v.name || v.id;
                  return (
                    <TableRow key={v.id}>
                      <TableCell>
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                          <span>{label}</span>
                          {v.isDefault && <Chip size="small" color="primary" label="Default" />}
                        </Stack>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {v.durationMinutes} min
                          {v.preBufferMinutes > 0 || v.postBufferMinutes > 0
                            ? ` · buffers ${v.preBufferMinutes}/${v.postBufferMinutes} min`
                            : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {currency ? formatMoney(v.price, currency) : v.price.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">{formatCredits(v.creditCost)}</TableCell>
                      <TableCell align="right">{noShowFeeLabel(v.noShowFee, currency)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconAction
                          size="small"
                          label={`Edit length ${label}`}
                          onClick={() => setDialog({ variation: v })}
                        >
                          <Icon name="edit" />
                        </IconAction>
                        <IconAction
                          size="small"
                          label={`Duplicate length ${label}`}
                          onClick={() => void handleDuplicate(v)}
                        >
                          <Icon name="content_copy" />
                        </IconAction>
                        <IconAction
                          size="small"
                          label={`Remove length ${label}`}
                          onClick={() => void handleDelete(v)}
                        >
                          <Icon name="delete" />
                        </IconAction>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </SummaryCard>

      <VariationDialog
        open={dialog !== null}
        serviceId={serviceId}
        variation={dialog?.variation ?? null}
        currency={currency}
        // The first variation is forced default; an existing-default edit can't be the
        // only default cleared (the dialog blocks that case).
        isOnlyDefault={defaults.length === 1}
        onClose={() => setDialog(null)}
        onSaved={(saved, wasDefault) => {
          setDialog(null);
          void onVariationSaved(saved, wasDefault);
        }}
        showToast={showToast}
      />
    </>
  );
}
